import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../hooks/useAuth";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import MayaLogo from "../components/MayaLogo";
import useUser from "../hooks/useUser";
import { Clock, Flame, Lightbulb, ArrowRight, BookOpen, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_medily-pro/artifacts/vaiy6q52_Picture6.png";

const StatTile = ({ icon: Icon, value, label, sub, mayaMark = false }) => (
  <div
    className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 flex items-center gap-4"
    data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <div className="w-14 h-14 rounded-full bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center relative shrink-0">
      {mayaMark ? <MayaLogo variant="on-white" className="h-6" /> : <Icon className="w-6 h-6" />}
    </div>
    <div className="min-w-0">
      <p className="text-[26px] font-semibold text-slate-900 leading-none">{value}</p>
      <p className="text-[13px] text-slate-600 mt-1">{label}</p>
      {sub && <p className="text-[12px] text-[#1d4ed8] mt-1">{sub}</p>}
    </div>
  </div>
);

const formatHours = (seconds) => {
  const h = Math.floor((seconds || 0) / 3600);
  const m = Math.floor(((seconds || 0) % 3600) / 60);
  if (h === 0 && m === 0) return "0h";
  if (h === 0) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { firstName } = useUser();
  const [tips, setTips] = useState([]);
  const [guides, setGuides] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [t, g, d] = await Promise.all([
          api.get("/tips").catch(() => ({ data: { tips: [] } })),
          api.get("/guides").catch(() => ({ data: { guides: [] } })),
          api.get("/me/dashboard").catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setTips(t.data.tips || []);
        setGuides(g.data.guides || []);
        setDashboard(d.data);
      } catch (_) { /* noop */ }
    })();
    return () => { mounted = false; };
  }, []);

  const readiness = dashboard?.interview_readiness ?? 0;
  const weakest = dashboard?.weakest_theme || null;
  const timeStudied = dashboard?.time_studied_seconds ?? 0;
  const dayStreak = dashboard?.day_streak ?? 0;
  const mayaCompleted = dashboard?.maya_interviews_completed ?? 0;
  const stationsUsed = dashboard?.stations_completed_period ?? 0;
  const stationsLimit = dashboard?.stations_limit ?? 15;

  const tipOfDay = useMemo(() => {
    if (!tips.length) return null;
    const day = Math.floor(Date.now() / (24 * 3600 * 1000));
    return tips[day % tips.length];
  }, [tips]);

  const recommendedResource = useMemo(() => {
    if (!weakest || !guides.length) return null;
    const w = weakest.toLowerCase();
    const eligible = guides.filter((g) => !/interview|tips/i.test((g.slug || "") + " " + (g.title || "")));
    for (const g of eligible) {
      if ((g.title || "").toLowerCase().includes(w)) return { guide: g, theme: weakest };
    }
    return eligible[0] ? { guide: eligible[0], theme: weakest } : null;
  }, [weakest, guides]);

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10">
          {/* Hero + Tip of the Day */}
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8 relative overflow-hidden">
              <div className="grid md:grid-cols-[1fr_minmax(180px,240px)] gap-6 items-stretch">
                <div className="min-w-0 flex flex-col">
                  <h1 className="text-[30px] md:text-[36px] leading-tight tracking-tight whitespace-nowrap" data-testid="dashboard-greeting">
                    <span
                      className="text-[#1d4ed8] pr-3"
                      style={{
                        fontFamily: "'Caveat', 'Kalam', 'Homemade Apple', cursive",
                        fontWeight: 700,
                        fontStyle: "italic",
                        fontSize: "1.15em",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {firstName}’s
                    </span>
                    {" "}
                    <span className="font-semibold text-slate-900">Preparation Hub</span>
                  </h1>
                  <div className="mt-5">
                    <div className="text-[13.5px] text-slate-500" data-testid="dashboard-readiness-label">
                      Interview Readiness
                    </div>
                    <div className="mt-1 flex items-baseline gap-3">
                      <span className="text-[42px] font-semibold text-[#1d4ed8] leading-none" data-testid="dashboard-readiness-value">
                        {readiness}%
                      </span>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-[#1d4ed8] rounded-full transition-all duration-500" style={{ width: `${readiness}%` }} />
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
                      <span>0%</span><span>50%</span><span>100%</span>
                    </div>
                  </div>
                </div>
                <img
                  src={HERO_IMG}
                  alt="Preparation illustration"
                  className="rounded-2xl w-full h-full object-cover self-stretch"
                  data-testid="dashboard-hero-img"
                />
              </div>
            </div>

            <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-7 flex flex-col">
              <div className="flex items-center gap-2 text-[#1d4ed8] font-semibold">
                <Lightbulb className="w-5 h-5" /> MMI Tip of the Day
              </div>
              {tipOfDay ? (
                <>
                  <p className="mt-4 font-semibold text-slate-900">{tipOfDay.title}</p>
                  <p className="mt-2 text-[14px] text-slate-600 leading-relaxed line-clamp-4">
                    {tipOfDay.description}
                  </p>
                  <button
                    onClick={() => navigate(`/interview-tips/${tipOfDay.slug}`)}
                    className="mt-auto pt-4 inline-flex items-center gap-1 text-[#1d4ed8] font-medium text-[13.5px] hover:gap-2 transition-all"
                  >
                    Read More <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <p className="mt-4 text-[13px] text-slate-500">Loading tip…</p>
              )}
            </div>
          </div>

          {/* Stats — 4 tiles per spec */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <StatTile icon={Clock} value={formatHours(timeStudied)} label="Time Studied" />
            <StatTile icon={Flame} value={dayStreak} label="Day Streak" sub={dayStreak ? "Keep it up!" : "Start today!"} />
            <StatTile mayaMark value={mayaCompleted} label="Maya Interviews Completed" />
            <StatTile icon={BookOpen} value={`${stationsUsed}/${stationsLimit}`} label="stations completed this month" />
          </div>

          {/* Maya's Recommendations */}
          <section className="mt-6 rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-[#1d4ed8]" />
              <h2 className="text-[20px] font-semibold text-slate-900">Maya’s Recommendations</h2>
            </div>

            {!weakest && (
              <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="maya-recommendation-empty">
                <div>
                  <p className="text-[16px] text-slate-800 font-medium">Hi {firstName}! 👋</p>
                  <p className="mt-2 text-slate-600 leading-relaxed max-w-xl">
                    I don’t know your strengths just yet. Complete your first Maya interview and I’ll personalise your study plan.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/maya")}
                  className="px-6 py-3 rounded-full bg-[#1d4ed8] text-white font-semibold hover:bg-[#1e40af] transition inline-flex items-center gap-2 shadow-sm justify-center"
                  data-testid="rec-start-maya-btn"
                >
                  <MayaLogo variant="on-blue" className="h-4" /> Start with Maya
                </button>
              </div>
            )}

            {weakest && recommendedResource && (
              <div className="grid lg:grid-cols-[1fr_2fr] gap-6" data-testid="maya-recommendation-personalized">
                <div>
                  <p className="text-slate-700 leading-relaxed">
                    Based on your past interviews, your weakest area is{" "}
                    <span className="font-semibold text-[#1d4ed8]">{weakest}</span>.
                  </p>
                  <p className="mt-3 text-slate-600 text-[13.5px]">Let’s focus on this topic to build your confidence.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => navigate(`/resources/${recommendedResource.guide.slug}`)}
                    className="text-left rounded-2xl border border-slate-100 p-4 hover:border-[#1d4ed8]/40 hover:shadow-md transition"
                    data-testid="rec-resource-btn"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center mb-2">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <p className="font-semibold text-slate-900 text-[14px]">{recommendedResource.guide.title}</p>
                    <p className="text-[12px] text-slate-500 mt-1">Strengthen your {weakest} understanding with the full guide.</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-[12.5px] text-[#1d4ed8] font-medium">
                      Open <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </button>
                  <button
                    onClick={() => navigate(`/maya`)}
                    className="text-left rounded-2xl border border-slate-100 p-4 hover:border-[#1d4ed8]/40 hover:shadow-md transition"
                    data-testid="rec-maya-station-btn"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center mb-2">
                      <MayaLogo variant="on-white" className="h-4" />
                    </div>
                    <p className="font-semibold text-slate-900 text-[14px]">Maya {weakest} Station</p>
                    <p className="text-[12px] text-slate-500 mt-1">Practise a realistic MMI station with Maya.</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-[12.5px] text-[#1d4ed8] font-medium">
                      Open <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </button>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
